import React, { Component } from 'react'
import moment from 'moment';
import { connect } from 'react-redux';
import { Spin, Input, Row, Col, Select, Badge, Switch, Button } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { load } from '../actions/BusSigns';
import { filterBusSigns, filterBusSignsByOperator, getDistinctOperators } from '../utils/busSignsFilter';
import BusSignsDeparturesButton from './BusSignsDeparturesButton';

const { Option } = Select;
const CZECHIA_CENTER = { lat: 49.8175, lng: 15.4730 };

const MARKER_COLORS = {
    default: { fill: '#1a73e8', stroke: '#0d47a1' },
    error: { fill: '#ff4d4f', stroke: '#a8071a' },
    warning: { fill: '#faad14', stroke: '#ad6800' },
    success: { fill: '#52c41a', stroke: '#237804' },
};

const getMarkerSvg = (colorKey) => {
    const { fill, stroke } = MARKER_COLORS[colorKey] || MARKER_COLORS.default;
    return `<svg xmlns="http://www.w3.org/2000/svg" width="34" height="46" viewBox="0 0 34 46">
    <path d="M17 0C7.61 0 0 7.61 0 17c0 12.75 17 29 17 29s17-16.25 17-29C34 7.61 26.39 0 17 0z" fill="${fill}" stroke="${stroke}" stroke-width="1"/>
    <circle cx="17" cy="16.5" r="6.5" fill="#ffffff"/>
</svg>`;
};

const getMarkerColorKey = (signLastCommunication) => {
    if (!signLastCommunication) {
        return 'default';
    }
    if (moment(signLastCommunication) <= moment().subtract(65, "minutes")) {
        return 'error';
    }
    if (moment(signLastCommunication) <= moment().subtract(35, "minutes")) {
        return 'warning';
    }
    return 'success';
};

class BusSignsMap extends Component {

    state = {
        searchText: '',
        selectedItem: null,
        scriptLoaded: false,
        selectedIds: null,
        selectedOperatorId: null,
        autoReload: (localStorage.getItem('map_autoReload') !== undefined && localStorage.getItem('map_autoReload') === 'true') ? true : false,
    }

    map = null;

    // Checking auto reload data
    _asyncRequest = this.intervalId = setInterval(() => {
        if (this.state.autoReload) {
            this.props.load();
        }
    }, 30000);
    _asyncRequest = this.props.load();

    componentWillUnmount() {
        clearInterval(this.intervalId);
    }

    handleSearchChange = (e) => {
        this.setState({ searchText: e.target.value });
    };

    handleSelectedIdsChange = (selectedIds) => {
        this.setState({ selectedIds });
    };

    handleOperatorChange = (selectedOperatorId) => {
        this.setState({ selectedOperatorId: selectedOperatorId || null, selectedIds: null });
    };

    getItemsWithPosition() {
        return (this.props.bussigns.items || [])
            .map(item => {
                if (!item.position || !item.position.coordinates) {
                    return null;
                }
                const lng = Number(item.position.coordinates[0]);
                const lat = Number(item.position.coordinates[1]);
                if (Number.isNaN(lat) || Number.isNaN(lng)) {
                    return null;
                }
                return { ...item, mapPosition: { lat, lng } };
            })
            .filter(Boolean);
    }

    getOperatorFilteredItems() {
        return filterBusSignsByOperator(this.getItemsWithPosition(), this.state.selectedOperatorId);
    }

    getFilteredItems() {
        const bySearch = filterBusSigns(this.getOperatorFilteredItems(), this.state.searchText);
        if (this.state.selectedIds === null) {
            return bySearch;
        }
        return bySearch.filter(item => this.state.selectedIds.includes(item.id));
    }

    fitToItems(items) {
        if (!this.map || !window.google || items.length === 0) {
            return;
        }
        if (items.length === 1) {
            this.map.setCenter(items[0].mapPosition);
            this.map.setZoom(15);
            return;
        }
        const bounds = new window.google.maps.LatLngBounds();
        items.forEach(item => {
            bounds.extend(item.mapPosition);
        });
        this.map.fitBounds(bounds);
    }

    onMapLoad = (map) => {
        this.map = map;
        this.fitToItems(this.getFilteredItems());
    };

    componentDidUpdate(prevProps, prevState) {
        if (
            prevProps.bussigns.items !== this.props.bussigns.items ||
            prevState.searchText !== this.state.searchText ||
            prevState.selectedIds !== this.state.selectedIds ||
            prevState.selectedOperatorId !== this.state.selectedOperatorId
        ) {
            this.fitToItems(this.getFilteredItems());
        }
    }

    render() {
        const itemsWithPosition = this.getItemsWithPosition();
        const operatorFilteredItems = this.getOperatorFilteredItems();
        const filteredItems = this.getFilteredItems();
        const operators = getDistinctOperators(itemsWithPosition);
        const selectedIds = this.state.selectedIds === null
            ? operatorFilteredItems.map(item => item.id)
            : this.state.selectedIds;
        const sortedItems = operatorFilteredItems.slice().sort((a, b) => a.busStopName.localeCompare(b.busStopName));
        return (
            <Spin spinning={this.props.bussigns.isLoading}>
                <Row style={{ marginBottom: 16 }}>
                    <Col flex="auto" style={{ display: 'flex', gap: 8 }}>
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            placeholder="Operátor"
                            value={this.state.selectedOperatorId}
                            onChange={this.handleOperatorChange}
                            style={{ width: '100%', maxWidth: 250 }}
                        >
                            {operators.map(operator => (
                                <Option key={operator.id} value={operator.id}>{operator.name}</Option>
                            ))}
                        </Select>
                        <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            maxTagCount={1}
                            maxTagTextLength={20}
                            placeholder="Zobrazené označníky"
                            value={selectedIds}
                            onChange={this.handleSelectedIdsChange}
                            style={{ width: '100%', maxWidth: 350 }}
                        >
                            {sortedItems.map(item => (
                                <Option key={item.id} value={item.id}>{item.busStopName} - {item.serialNumber}</Option>
                            ))}
                        </Select>
                        <Input.Search
                            allowClear
                            placeholder="Hledat dle seriového čísla nebo názvu zastávky"
                            value={this.state.searchText}
                            onChange={this.handleSearchChange}
                            style={{ maxWidth: 350 }}
                        />
                    </Col>
                    <Col flex="none" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <Switch
                            checked={this.state.autoReload}
                            onChange={(checked) => {
                                localStorage.setItem('map_autoReload', checked);
                                this.setState({
                                    autoReload: checked
                                });
                            }} /> Automatická obnova
                        <Button
                            type="default"
                            icon={<SyncOutlined />}
                            onClick={() => { this.props.load() }}
                            style={{ marginRight: 10, marginLeft: 15 }}>
                        </Button>
                    </Col>
                </Row>
                <LoadScript
                    googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                    onLoad={() => this.setState({ scriptLoaded: true })}
                >
                    <GoogleMap
                        mapContainerStyle={{ width: '100%', height: '70vh' }}
                        center={CZECHIA_CENTER}
                        zoom={7}
                        onLoad={this.onMapLoad}
                    >
                        {this.state.scriptLoaded && filteredItems.map(item => (
                            <Marker
                                key={item.id}
                                position={item.mapPosition}
                                title={item.serialNumber}
                                icon={{
                                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(getMarkerSvg(getMarkerColorKey(item.signLastCommunication))),
                                    scaledSize: new window.google.maps.Size(30.6, 41.4),
                                    anchor: new window.google.maps.Point(15.3, 41.4),
                                }}
                                onClick={() => this.setState({ selectedItem: item })}
                            />
                        ))}
                        {this.state.scriptLoaded && this.state.selectedItem &&
                            <InfoWindow
                                position={this.state.selectedItem.mapPosition}
                                options={{ pixelOffset: new window.google.maps.Size(0, -40) }}
                                onCloseClick={() => this.setState({ selectedItem: null })}
                            >
                                <div style={{ width: 300 }}>
                                    <div style={{ fontWeight: 'bold', fontSize: 14, marginBottom: 14 }}>{this.state.selectedItem.serialNumber}</div>
                                    <div>{this.state.selectedItem.busStopName}</div>
                                    <div style={{ whiteSpace: 'nowrap', marginTop: 8 }}>
                                        Poslední komunikace:
                                        {this.state.selectedItem.signLastCommunication
                                            ? <span style={{ marginLeft: 10 }}>
                                                <Badge color={MARKER_COLORS[getMarkerColorKey(this.state.selectedItem.signLastCommunication)].fill} />
                                                <span style={{ marginLeft: -2 }}>
                                                    {moment(this.state.selectedItem.signLastCommunication).format('DD. MM. YYYY HH:mm')}
                                                </span>
                                            </span>
                                            : ' -'
                                        }
                                    </div>
                                    <div style={{ textAlign: 'center', marginTop: 12, marginBottom: 8 }}>
                                        <BusSignsDeparturesButton serialNumber={this.state.selectedItem.serialNumber} />
                                    </div>
                                </div>
                            </InfoWindow>
                        }
                    </GoogleMap>
                </LoadScript>
            </Spin>
        );
    }
}
const mapStateToProps = state => ({
    bussigns: state.bussigns
});
export default connect(
    mapStateToProps,
    { load }
)(BusSignsMap)
